import { Box, Button, Stack, Typography } from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import { Provider } from "../../types/connecteeSubmission";
import { ProviderCard } from "./ProviderCard";

const TEAL = "#008080";
const MINT_BG = "#F4F9F8";

interface FallbackMatchesViewProps {
  firstName: string;
  providers: Provider[];
  onRefineSearch: () => void;
}

export function FallbackMatchesView({
  firstName,
  providers,
  onRefineSearch,
}: FallbackMatchesViewProps) {
  return (
    <Box
      sx={{
        bgcolor: MINT_BG,
        width: "100%",
        minHeight: "100%",
        py: { xs: 3, md: 4 },
        px: { xs: 2, md: 3 },
      }}
    >
      <Stack spacing={4} sx={{ maxWidth: 1000, mx: "auto", width: "100%" }}>
        <Stack spacing={1.5} alignItems="center" textAlign="center" px={1}>
          <Typography variant="h4" fontWeight={700} color="#212121">
            Hi {firstName}, we've identified some great potential fits for you.
          </Typography>
          <Typography variant="body1" color="#616161" maxWidth={760}>
            While we weren't able to find a provider located in the specific area you're looking
            for, we've identified a few providers who may still be a great fit based on your other
            criteria.
          </Typography>
        </Stack>

        <Stack spacing={2}>
          {providers.map((provider) => (
            <ProviderCard key={provider.id} provider={provider} layout="horizontal" />
          ))}
        </Stack>

        <Box
          sx={{
            bgcolor: "#E8F0EF",
            borderRadius: 2,
            p: 3,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 1.5,
              bgcolor: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <TuneIcon sx={{ color: "#A63D7E" }} />
          </Box>

          <Box flex={1}>
            <Typography variant="subtitle1" fontWeight={700} color="#212121">
              Not feeling these matches?
            </Typography>
            <Typography variant="body2" color="#616161">
              Feel free to resubmit the form with updated or more flexible criteria. This can open
              up additional options and help us find a better match for you.
            </Typography>
          </Box>

          <Button
            variant="outlined"
            onClick={onRefineSearch}
            sx={{
              borderColor: TEAL,
              color: TEAL,
              textTransform: "none",
              fontWeight: 600,
              whiteSpace: "nowrap",
              flexShrink: 0,
              flexDirection: "row",
              bgcolor: "#fff",
              "&:hover": { borderColor: TEAL, bgcolor: "rgba(0, 128, 128, 0.04)" },
            }}
          >
            Refine My Search
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
